import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

let accessTokenPromise = null;
const ACTIVE_ACCOUNT_STORAGE_KEY = 'proofyx_active_account_id';

const APP_STORAGE_PREFIXES = [
  'onboarding_state_',
];

const APP_STORAGE_KEYS = [
  'organization_id',
  'assessment_state',
  'assessment_states',
  'ai_query_history',
  'reportReturnState',
  ACTIVE_ACCOUNT_STORAGE_KEY,
];

export const getAccountId = (account) =>
  account?.homeAccountId || account?.localAccountId || account?.username || null;

const rememberActiveAccount = (account) => {
  const accountId = getAccountId(account);
  if (accountId) {
    localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, accountId);
  }
};

const findRememberedAccount = (accounts) => {
  const rememberedAccountId = localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
  if (!rememberedAccountId) return null;

  return accounts.find((account) => getAccountId(account) === rememberedAccountId) || null;
};

export const resolveActiveAccount = (instance, preferredAccount = null) => {
  if (!instance) return null;

  if (preferredAccount) {
    instance.setActiveAccount(preferredAccount);
    rememberActiveAccount(preferredAccount);
    return preferredAccount;
  }

  const activeAccount = instance.getActiveAccount();
  if (activeAccount) {
    rememberActiveAccount(activeAccount);
    return activeAccount;
  }

  const accounts = instance.getAllAccounts();
  const rememberedAccount = findRememberedAccount(accounts);
  if (rememberedAccount) {
    instance.setActiveAccount(rememberedAccount);
    return rememberedAccount;
  }

  if (accounts.length === 1) {
    instance.setActiveAccount(accounts[0]);
    rememberActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
};

export const setActiveAccountFromResult = (instance, result) => {
  if (result?.account) {
    instance.setActiveAccount(result.account);
    rememberActiveAccount(result.account);
    return result.account;
  }
  return resolveActiveAccount(instance);
};

export const acquireAccessToken = async (instance) => {
  const account = resolveActiveAccount(instance);

  if (!account) {
    throw new Error('No active account. Please sign in again.');
  }

  if (!accessTokenPromise) {
    accessTokenPromise = instance.acquireTokenSilent({
      ...apiRequest,
      account,
    }).finally(() => {
      accessTokenPromise = null;
    });
  }

  try {
    const response = await accessTokenPromise;
    return response.accessToken;
  } catch (error) {
    accessTokenPromise = null;
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect({
        ...apiRequest,
        account,
      });
    }
    throw error;
  }
};

export const clearProofyxAppStorage = () => {
  APP_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key && APP_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  }
};

export const forceLogout = async (instance, options = {}) => {
  const account = resolveActiveAccount(instance);

  clearProofyxAppStorage();
  sessionStorage.clear();

  try {
    await instance.logoutRedirect({
      account: account || undefined,
      postLogoutRedirectUri: options.postLogoutRedirectUri || '/login',
    });
  } catch (error) {
    console.error('Logout redirect failed:', error);
    window.location.href = '/login';
  }
};
